import { CachedOrbit } from "./CachedOrbit.js";
import { J2000 } from "../../AstroConstants.js";
import { Vector3 } from "../../../math/Vector3.js";
import { Deg2Rad } from "../../../math/MathEx.js";
import { BodyMath } from "../../bodies/BodyMath.js";
import { IAULunarRotationModel } from "../rotation/IAULunarRotationModel.js";
class LunarOrbit extends CachedOrbit {
    computePosition(jd) {
        let jd19, t, t2;
        let ld, ms, md, de, f, n, hp;
        let a, sa, sn, b, sb, c, sc, e, e2, l, g, w1, w2;
        let m1, m2, m3, m4, m5, m6;
        let eclLon, eclLat, horzPar, distance;
        const coords = { ra: 0, dec: 0 };
        // Computation requires an abbreviated Julian day:
        // epoch January 0.5, 1900.
        jd19 = jd - 2415020.0;
        t = jd19 / 36525;
        t2 = t * t;
        m1 = jd19 / 27.32158213;
        m1 = 360.0 * (m1 - Math.floor(m1));
        m2 = jd19 / 365.2596407;
        m2 = 360.0 * (m2 - Math.floor(m2));
        m3 = jd19 / 27.55455094;
        m3 = 360.0 * (m3 - Math.floor(m3));
        m4 = jd19 / 29.53058868;
        m4 = 360.0 * (m4 - Math.floor(m4));
        m5 = jd19 / 27.21222039;
        m5 = 360.0 * (m5 - Math.floor(m5));
        m6 = jd19 / 6798.363307;
        m6 = 360.0 * (m6 - Math.floor(m6));
        ld = 270.434164 + m1 - (0.001133 - 0.0000019 * t) * t2;
        ms = 358.475833 + m2 - (0.00015 + 0.0000033 * t) * t2;
        md = 296.104608 + m3 + (0.009192 + 0.0000144 * t) * t2;
        de = 350.737486 + m4 - (0.001436 - 0.0000019 * t) * t2;
        f = 11.250889 + m5 - (0.003211 + 0.0000003 * t) * t2;
        n = 259.183275 - m6 + (0.002078 + 0.000022 * t) * t2;
        a = Deg2Rad * (51.2 + 20.2 * t);
        sa = Math.sin(a);
        sn = Math.sin(Deg2Rad * n);
        b = 346.56 + (132.87 - 0.0091731 * t) * t;
        sb = 0.003964 * Math.sin(Deg2Rad * b);
        c = Deg2Rad * (n + 275.05 - 2.3 * t);
        sc = Math.sin(c);
        ld = ld + 0.000233 * sa + sb + 0.001964 * sn;
        ms = ms - 0.001778 * sa;
        md = md + 0.000817 * sa + sb + 0.002541 * sn;
        f = f + sb - 0.024691 * sn - 0.004328 * sc;
        de = de + 0.002011 * sa + sb + 0.001964 * sn;
        e = 1 - (0.002495 + 7.52e-6 * t) * t;
        e2 = e * e;
        ld = Deg2Rad * ld;
        ms = Deg2Rad * ms;
        n = Deg2Rad * n;
        de = Deg2Rad * de;
        f = Deg2Rad * f;
        md = Deg2Rad * md;
        l =
            6.28875 * Math.sin(md) +
                1.27402 * Math.sin(2 * de - md) +
                0.658309 * Math.sin(2 * de) +
                0.213616 * Math.sin(2 * md) -
                e * 0.185596 * Math.sin(ms) -
                0.114336 * Math.sin(2 * f) +
                0.058793 * Math.sin(2 * (de - md)) +
                0.057212 * e * Math.sin(2 * de - ms - md) +
                0.05332 * Math.sin(2 * de + md) +
                0.045874 * e * Math.sin(2 * de - ms) +
                0.041024 * e * Math.sin(md - ms);
        l =
            l -
                0.034718 * Math.sin(de) -
                e * 0.030465 * Math.sin(ms + md) +
                0.015326 * Math.sin(2 * (de - f)) -
                0.012528 * Math.sin(2 * f + md) -
                0.01098 * Math.sin(2 * f - md) +
                0.010674 * Math.sin(4 * de - md) +
                0.010034 * Math.sin(3 * md) +
                0.008548 * Math.sin(4 * de - 2 * md) -
                e * 0.00791 * Math.sin(ms - md + 2 * de) -
                e * 0.006783 * Math.sin(2 * de + ms);
        l =
            l +
                0.005162 * Math.sin(md - de) +
                e * 0.005 * Math.sin(ms + de) +
                0.003862 * Math.sin(4 * de) +
                e * 0.004049 * Math.sin(md - ms + 2 * de) +
                0.003996 * Math.sin(2 * (md + de)) +
                0.003665 * Math.sin(2 * de - 3 * md) +
                e * 0.002695 * Math.sin(2 * md - ms) +
                0.002602 * Math.sin(md - 2 * (f + de)) +
                e * 0.002396 * Math.sin(2 * (de - md) - ms) -
                0.002349 * Math.sin(md + de);
        l =
            l +
                e2 * 0.002249 * Math.sin(2 * (de - ms)) -
                e * 0.002125 * Math.sin(2 * md + ms) -
                e2 * 0.002079 * Math.sin(2 * ms) +
                e2 * 0.002059 * Math.sin(2 * (de - ms) - md) -
                0.001773 * Math.sin(md + 2 * (de - f)) -
                0.001595 * Math.sin(2 * (f + de)) +
                e * 0.00122 * Math.sin(4 * de - ms - md) -
                0.00111 * Math.sin(2 * (md + f)) +
                0.000892 * Math.sin(md - 3 * de);
        l =
            l -
                e * 0.000811 * Math.sin(ms + md + 2 * de) +
                e * 0.000761 * Math.sin(4 * de - ms - 2 * md) +
                e2 * 0.000704 * Math.sin(md - 2 * (ms + de)) +
                e * 0.000693 * Math.sin(ms - 2 * (md - de)) +
                e * 0.000598 * Math.sin(2 * (de - f) - ms) +
                0.00055 * Math.sin(md + 4 * de) +
                0.000538 * Math.sin(4 * md) +
                e * 0.000521 * Math.sin(4 * de - ms) +
                0.000486 * Math.sin(2 * md - de);
        l = l + e2 * 0.000717 * Math.sin(md - 2 * ms);
        eclLon = ld + Deg2Rad * l;
        eclLon = LunarOrbit.Pfmod(eclLon, 2 * Math.PI);
        g =
            5.12819 * Math.sin(f) +
                0.280606 * Math.sin(md + f) +
                0.277693 * Math.sin(md - f) +
                0.173238 * Math.sin(2 * de - f) +
                0.055413 * Math.sin(2 * de + f - md) +
                0.046272 * Math.sin(2 * de - f - md) +
                0.032573 * Math.sin(2 * de + f) +
                0.017198 * Math.sin(2 * md + f) +
                0.009267 * Math.sin(2 * de + md - f) +
                0.008823 * Math.sin(2 * md - f) +
                e * 0.008247 * Math.sin(2 * de - ms - f);
        g =
            g +
                0.004323 * Math.sin(2 * (de - md) - f) +
                0.0042 * Math.sin(2 * de + f + md) +
                e * 0.003372 * Math.sin(f - ms - 2 * de) +
                e * 0.002472 * Math.sin(2 * de + f - ms - md) +
                e * 0.002222 * Math.sin(2 * de + f - ms) +
                e * 0.002072 * Math.sin(2 * de - f - ms - md) +
                e * 0.001877 * Math.sin(f - ms + md) +
                0.001828 * Math.sin(4 * de - f - md) -
                e * 0.001803 * Math.sin(f + ms) -
                0.00175 * Math.sin(3 * f);
        g =
            g +
                e * 0.00157 * Math.sin(md - ms - f) -
                0.001487 * Math.sin(f + de) -
                e * 0.001481 * Math.sin(f + ms + md) +
                e * 0.001417 * Math.sin(f - ms - md) +
                e * 0.00135 * Math.sin(f - ms) +
                0.00133 * Math.sin(f - de) +
                0.001106 * Math.sin(f + 3 * md) +
                0.00102 * Math.sin(4 * de - f) +
                0.000833 * Math.sin(f + 4 * de - md) +
                0.000781 * Math.sin(md - 3 * f) +
                0.00067 * Math.sin(f + 4 * de - 2 * md);
        g =
            g +
                0.000606 * Math.sin(2 * de - 3 * f) +
                0.000597 * Math.sin(2 * (de + md) - f) +
                e * 0.000492 * Math.sin(2 * de + md - ms - f) +
                0.00045 * Math.sin(2 * (md - de) - f) +
                0.000439 * Math.sin(3 * md - f) +
                0.000423 * Math.sin(f + 2 * (de + md)) +
                0.000422 * Math.sin(2 * de - f - 3 * md) -
                e * 0.000367 * Math.sin(ms + f + 2 * de - md) -
                e * 0.000353 * Math.sin(ms + f + 2 * de) +
                0.000331 * Math.sin(f + 4 * de);
        g =
            g +
                e * 0.000317 * Math.sin(2 * de + f - ms + md) +
                e2 * 0.000306 * Math.sin(2 * (de - ms) - f) -
                0.000283 * Math.sin(md + 3 * f);
        w1 = 0.0004664 * Math.cos(n);
        w2 = 0.0000754 * Math.cos(c);
        eclLat = Deg2Rad * g * (1 - w1 - w2);
        hp =
            0.950724 +
                0.051818 * Math.cos(md) +
                0.009531 * Math.cos(2 * de - md) +
                0.007843 * Math.cos(2 * de) +
                0.002824 * Math.cos(2 * md) +
                0.000857 * Math.cos(2 * de + md) +
                e * 0.000533 * Math.cos(2 * de - ms) +
                e * 0.000401 * Math.cos(2 * de - md - ms) +
                e * 0.00032 * Math.cos(md - ms) -
                0.000271 * Math.cos(de) -
                e * 0.000264 * Math.cos(ms + md) -
                0.000198 * Math.cos(2 * f - md);
        hp =
            hp +
                0.000173 * Math.cos(3 * md) +
                0.000167 * Math.cos(4 * de - md) -
                e * 0.000111 * Math.cos(ms) +
                0.000103 * Math.cos(4 * de - 2 * md) -
                0.000084 * Math.cos(2 * md - 2 * de) -
                e * 0.000083 * Math.cos(2 * de + ms) +
                0.000079 * Math.cos(2 * de + 2 * md) +
                0.000072 * Math.cos(4 * de) +
                e * 0.000064 * Math.cos(2 * de - ms + md) -
                e * 0.000063 * Math.cos(2 * de + ms - md) +
                e * 0.000041 * Math.cos(ms + de);
        hp =
            hp +
                e * 0.000035 * Math.cos(2 * md - ms) -
                0.000033 * Math.cos(3 * md - 2 * de) -
                0.00003 * Math.cos(md + de) -
                0.000029 * Math.cos(2 * (f - de)) -
                e * 0.000029 * Math.cos(2 * md + ms) +
                e2 * 0.000026 * Math.cos(2 * (de - ms)) -
                0.000023 * Math.cos(2 * (f - de) + md) +
                e * 0.000019 * Math.cos(4 * de - ms - md);
        horzPar = Deg2Rad * hp;
        // At this point we have values of ecliptic longitude, latitude and
        // horizontal parallax (eclLong, eclLat, horzPar) in radians.
        // Now compute distance using horizontal parallax.
        distance = 6378.14 / Math.sin(horzPar);
        // Finally convert eclLat, eclLon to RA, Dec.
        LunarOrbit.EclipticToEquatorial(t, eclLat, eclLon, coords);
        // RA and Dec are referred to the equinox of date; we want to use
        // the J2000 equinox instead.  A better idea would be to directly
        // compute the position of the Moon in this coordinate system, but
        // this was easier.
        LunarOrbit.EpochConvert(jd, J2000, coords.ra, coords.dec, coords);
        // Corrections for internal coordinate system
        coords.dec -= Math.PI / 2;
        coords.ra += Math.PI;
        return new Vector3(Math.cos(coords.ra) * Math.sin(coords.dec) * distance, Math.cos(coords.dec) * distance, -Math.sin(coords.ra) * Math.sin(coords.dec) * distance);
        //// Skip the conversion and return ecliptical coordinates
        //double x = distance * Math.cos(eclLat) * Math.cos(eclLon);
        //double y = distance * Math.cos(eclLat) * Math.sin(eclLon);
        //double z = distance * Math.sin(eclLat);
        //return Point3d(x, z, -y);
    }
    getPeriod() {
        return 27.321661;
    }
    getBoundingRadius() {
        return 405504 * this.BoundingRadiusSlack;
    }
}
export function createMoonBodyMath() {
    return new BodyMath(new LunarOrbit(), new IAULunarRotationModel(), BodyMath.EquatorFrame);
    // return new BodyMath(
    //     new LunarElp82Orbit(),
    //     new IAULunarRotationModel(),
    //     BodyMath.EquatorFrame,
    //     BodyMath.EclipticFrame,
    // );
}
